import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { HelpCircle, BookOpen, PlayCircle, FileText, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { clientPortalService } from '../../services/clientPortal.service';
import { helpArticleForPath } from '../../tour/helpArticleMap';
import { useTour } from '../../tour/TourProvider';

// Header "Help & Tour" control: a context-aware "?" + replay-tour + Help Center.
const HelpButton = () => {
  const tour = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [article, setArticle] = useState(null);

  const openContextual = async () => {
    const slug = helpArticleForPath(location.pathname);
    if (!slug) {
      navigate('/client/help');
      return;
    }
    setOpen(true);
    setLoading(true);
    setArticle(null);
    try {
      const data = await clientPortalService.getHelpArticle(slug);
      setArticle(data);
    } catch (e) {
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-amber-600"
            aria-label="Help and tour"
            data-testid="help-tour-btn"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Help &amp; Tour</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={openContextual} data-testid="help-this-page-btn">
            <FileText className="h-4 w-4" />
            Help for this page
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/client/help')} data-testid="help-open-center-btn">
            <BookOpen className="h-4 w-4" />
            Open Help Center
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => tour?.replayTour()} data-testid="help-replay-tour-btn">
            <PlayCircle className="h-4 w-4" />
            Replay tour
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="help-drawer">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : article ? (
            <>
              <SheetHeader className="text-left">
                <SheetTitle data-testid="help-drawer-title">{article.title}</SheetTitle>
                {article.summary && <SheetDescription>{article.summary}</SheetDescription>}
              </SheetHeader>
              <div
                className="prose prose-sm max-w-none mt-6 text-gray-700"
                data-testid="help-drawer-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html || '') }}
              />
              <Button
                variant="outline"
                className="mt-6 w-full"
                onClick={() => {
                  setOpen(false);
                  navigate(`/client/help?article=${article.slug}`);
                }}
                data-testid="help-drawer-open-full-btn"
              >
                Open full article in Help Center
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          ) : (
            <div className="py-20 text-center text-gray-500" data-testid="help-drawer-empty">
              No help article found for this page.
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default HelpButton;
